import z from 'zod';
import {
  ApiClient,
  ApiClientContext,
  ApiClientOptions,
  RouteOptions,
  defaultAxiosConfig,
} from '@qualifio/api-client';
import { AxiosInstance } from 'axios';
import { $bool } from '../zod';
import {
  AccountAttributes,
  AddProgramInput,
  CurrencyAttributes,
  DomainAttributes,
  LevelsConfigAttributes,
  ProgramAttributes,
  SetCurrencyBody,
  SetLevelConfigBody,
} from '../gamification-api-client';
import {
  AddManagerIncomingWebhookBody,
  DynamicConfiguration,
  ExportProfileOptions,
  LoyaltyProgramEmbed,
  ManagerIncomingWebhookAttributes,
  UpdateManagerIncomingWebhookBody,
} from './types/types';
import { ManageAccountQueryParams, UpsertAccountBody } from './types/organizations';
import { RewardAttributes } from '../rewards-api-client';

export const $DeleteProfileQueryParamsSchema = z.object({
  forceDelete: $bool.optional().default(false),
});

export type DeleteProfileQueryParams = z.infer<typeof $DeleteProfileQueryParamsSchema>;

export class ManagerApiClient extends ApiClient {
  static serviceName = 'manager-api';

  constructor(http?: AxiosInstance, context?: ApiClientContext, options?: ApiClientOptions) {
    super('/api/manager', http, context, options);
  }

  async upsertAccount(
    body: UpsertAccountBody,
    options: RouteOptions<ManageAccountQueryParams>,
  ): Promise<void> {
    await this.http.post('/accounts', body, defaultAxiosConfig(options));
  }

  async setupAccount(setupAccountId: string, options?: RouteOptions): Promise<AccountAttributes> {
    const { data } = await this.http.post(
      `/accounts/${setupAccountId}`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async addIncomingWebhook(
    programId: string,
    body: AddManagerIncomingWebhookBody,
    options?: RouteOptions,
  ): Promise<ManagerIncomingWebhookAttributes> {
    const { data } = await this.http.post(
      `/programs/${programId}/incoming-webhooks`,
      body,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async deleteIncomingWebhook(webhookId: string, options?: RouteOptions): Promise<void> {
    await this.http.delete(`/incoming-webhooks/${webhookId}`, defaultAxiosConfig(options));
  }

  async updateIncomingWebhook(
    body: UpdateManagerIncomingWebhookBody,
    options?: RouteOptions,
  ): Promise<ManagerIncomingWebhookAttributes> {
    const { data } = await this.http.put(
      `/incoming-webhooks/${body.id}`,
      body,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getEmbed(programId: string, options?: RouteOptions): Promise<LoyaltyProgramEmbed> {
    const { data } = await this.http.get(
      `/programs/${programId}/embeded`,
      defaultAxiosConfig(options),
    );
    return {
      script: '',
      iframe: `<iframe id='qualifio_loyalty_${programId}' src='${data.url}'></iframe>`,
      webview: data.url,
    };
  }

  async getIncomingWebhook(
    webhookId: string,
    options?: RouteOptions,
  ): Promise<ManagerIncomingWebhookAttributes> {
    const { data } = await this.http.get(
      `/incoming-webhooks/${webhookId}`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getProgramIncomingWebhooks(
    programId: string,
    options?: RouteOptions,
  ): Promise<ManagerIncomingWebhookAttributes[]> {
    const { data } = await this.http.get(
      `/programs/${programId}/incoming-webhooks`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async getAccountIncomingWebhooks(
    accountId: string,
    options?: RouteOptions,
  ): Promise<ManagerIncomingWebhookAttributes[]> {
    const { data } = await this.http.get(
      `/accounts/${accountId}/incoming-webhooks`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async archiveReward(rewardId: string, options?: RouteOptions): Promise<RewardAttributes> {
    const { data } = await this.http.delete(`/rewards/${rewardId}`, defaultAxiosConfig(options));
    return data;
  }

  async addProgram(
    accountId: string,
    program: AddProgramInput,
    options?: RouteOptions,
  ): Promise<ProgramAttributes> {
    const { data } = await this.http.post(
      `/accounts/${accountId}/programs`,
      program,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async resetProgram(id: string, name: string, options?: RouteOptions): Promise<ProgramAttributes> {
    const { data } = await this.http.post(
      `/programs/${id}/reset`,
      { name },
      defaultAxiosConfig(options),
    );
    return data;
  }

  async deleteProgram(id: string, options?: RouteOptions): Promise<ProgramAttributes> {
    const { data } = await this.http.delete(`/programs/${id}`, defaultAxiosConfig(options));
    return data;
  }

  async getConfiguration(options?: RouteOptions): Promise<DynamicConfiguration> {
    const { data } = await this.http.get('/configuration', defaultAxiosConfig(options));
    return data;
  }

  async setupSubdomain(
    domain: DomainAttributes,
    options?: RouteOptions,
  ): Promise<DomainAttributes> {
    const { data } = await this.http.post(
      `/domains/${domain.id}/setup-domain`,
      { domain },
      defaultAxiosConfig(options),
    );

    return data;
  }

  async deleteSubdomain(
    domain: DomainAttributes,
    options?: RouteOptions,
  ): Promise<DomainAttributes> {
    const { data } = await this.http.delete(`/domains/${domain.id}`, defaultAxiosConfig(options));
    return data;
  }

  async deleteLevel(
    programId: string,
    levelId: string,
    options?: RouteOptions,
  ): Promise<{ updatedMemberships: number; deletedRules: number; levelDeleted: boolean }> {
    return this.http.delete(
      `/programs/${programId}/levels/${levelId}`,
      defaultAxiosConfig(options),
    );
  }

  async deleteCustomKey(
    customKeyId: string,
    options?: RouteOptions,
  ): Promise<{ deletedRules: number; customKeyDeleted: boolean }> {
    return this.http.delete(`/custom-keys/${customKeyId}`, defaultAxiosConfig(options));
  }

  async disableSocialLogin(programId: string, options?: RouteOptions): Promise<ProgramAttributes> {
    const { data } = await this.http.delete(
      `/programs/${programId}/setup-social-login`,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async setLevelsConfig(
    programId: string,
    body: SetLevelConfigBody,
    options?: RouteOptions,
  ): Promise<LevelsConfigAttributes> {
    const { data } = await this.http.put(
      `/programs/${programId}/levels-config`,
      body,
      defaultAxiosConfig(options),
    );
    return data;
  }

  async setCurrency(
    programId: string,
    body: SetCurrencyBody,
    options?: RouteOptions,
  ): Promise<CurrencyAttributes> {
    const { data } = await this.http.put(
      `/programs/${programId}/currencies`,
      body,
      defaultAxiosConfig(options),
    );
    return data;
  }

  // https://stackoverflow.com/a/53230807
  async exportProfiles(
    programId: string,
    options?: RouteOptions<ExportProfileOptions>,
  ): Promise<void> {
    const response = await this.http.get(`/programs/${programId}/export-profiles`, {
      ...defaultAxiosConfig(options),
      responseType: 'blob',
    });

    const href = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', 'profiles.csv');
    document.body.appendChild(link);
    link.click();

    // clean up "a" element & remove ObjectURL
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  }
}
